<template>
    <ion-page>
        <base-header title="Clienti" :show-menu="true" :buttons="headerButtons"></base-header>
        <ion-content :fullscreen="true" style="display: flex; flex-direction: column">
            <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
                <ion-refresher-content></ion-refresher-content>
            </ion-refresher>
            <ion-header collapse="condense">
                <ion-toolbar>
                    <ion-title size="large">Clienti</ion-title>
                </ion-toolbar>
            </ion-header>
            <ion-searchbar v-model="search" placeholder="Cerca cliente"></ion-searchbar>
            <div class="ion-padding-horizontal">
                <map-box
                    ref="mapBox"
                    :max-zoom="18"
                    :markers-data="costumersMarkers"
                    :disable-interactions="true"></map-box>
            </div>
            <div
                class="ion-padding-horizontal ion-margin-top-half ion-margin-bottom-half"
                style="display: flex; justify-content: space-between">
                <ion-button
                    fill="clear"
                    style="--color: var(--ion-color-dark)"
                    @click="$router.push({ name: 'zones' })">
                    <ion-icon :icon="mapOutline" slot="start"></ion-icon>
                    <span>Aree</span>
                </ion-button>
                <ion-button fill="clear" style="--color: var(--ion-color-dark)" @click="goToArchivedCostumers()">
                    <ion-icon :icon="archiveOutline" slot="start"></ion-icon>
                    <span>Archiviati</span>
                </ion-button>
            </div>
            <ion-list v-if="!areCostumersLoading" class="costumer-list-item">
                <ion-item-sliding v-for="costumer in costumers">
                    <ion-item-options side="start">
                        <ion-item-option color="danger" @click="archiveCostumer(costumer.id)">
                            <ion-icon slot="icon-only" :icon="archiveOutline"></ion-icon>
                            <span>Archivia</span>
                        </ion-item-option>
                    </ion-item-options>
                    <ion-item
                        class="costumer-list-item ion-activatable ripple-parent"
                        @click="router.push({ name: 'costumerDetails', params: { costumerId: costumer.id } })">
                        <ion-label class="costumer-list-item-label">
                            <ion-text>
                                <h2>
                                    <b>{{ costumer.business_name }}</b>
                                </h2>
                            </ion-text>
                            <ion-note>
                                <h5>{{ costumer.city }}</h5>
                            </ion-note>
                        </ion-label>
                        <ion-ripple-effect></ion-ripple-effect>
                        <div v-if="costumer.last_inspection_delta" slot="end">
                            <small :class="`expiration-indicator ${costumer.expiration_state}`">
                                <span v-if="costumer.expiration_state">
                                    {{ costumer.last_inspection_delta }} giorni fa
                                </span>
                                <ion-note v-else>
                                    <em>{{ costumer.last_inspection_delta }} giorni fa</em>
                                </ion-note>
                            </small>
                        </div>
                    </ion-item>
                </ion-item-sliding>
                <ion-item v-if="costumers.length === 0 && search.length !== 0" class="ion-text-center" lines="none">
                    <ion-text color="medium" class="w-full">
                        <h3>Nessun cliente trovato</h3>
                        <p>Prova a modificare i criteri di ricerca o ad aggiungerne uno nuovo.</p>
                    </ion-text>
                </ion-item>
            </ion-list>
            <ion-list v-else class="costumer-list-item">
                <ion-item class="costumer-list-item" v-for="index in [1, 2, 3, 4, 5, 6, 7, 8, 9]">
                    <ion-label>
                        <h3>
                            <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                        </h3>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                        </p>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 30%"></ion-skeleton-text>
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
            <ion-fab v-if="isOnline" slot="fixed" vertical="bottom" horizontal="end">
                <ion-fab-button @click="openModal">
                    <ion-icon :icon="add"></ion-icon>
                </ion-fab-button>
            </ion-fab>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import {
    IonPage,
    IonSearchbar,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonText,
    IonItem,
    IonSkeletonText,
    IonNote,
    IonLabel,
    IonRippleEffect,
    IonFabButton,
    IonIcon,
    IonFab,
    IonButton,
    modalController,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
    alertController,
    toastController,
    IonRefresher,
    IonRefresherContent,
} from "@ionic/vue";
import { useCostumersStore } from "@/stores/costumers";
import { computed, onMounted, ref } from "vue";
import router from "@/router";
import BaseHeader from "@/components/BaseHeader.vue";
import { useInspectionsStore } from "@/stores/inspections";
import { add, archive, archiveOutline, cloudDoneOutline, cloudOfflineOutline, mapOutline } from "ionicons/icons";
import { useNetworkStore } from "@/stores/network";
import CostumerForm from "@/components/modals/CostumerForm.vue";
import MapBox from "@/components/MapBox.vue";
import { getMarkerColor, getMarkerIconUrl } from "@/types/intervention_frequency";
import { IExtendedMarkerOptions } from "@/types/maps";
import CostumerModal from "@/components/modals/CostumerModal.vue";
import { ICostumer } from "@/types/costumer";
import * as Sentry from "@sentry/vue";

const costumersStore = useCostumersStore();
const networkStore = useNetworkStore();
const inspectionsStore = useInspectionsStore();
const mapBox = ref<InstanceType<typeof MapBox> | null>(null);

const search = ref<string>("");
const areCostumersLoading = computed(() => costumersStore.isLoading);
const costumers = computed(() =>
    costumersStore.getCostumers
        .filter(
            (costumers) =>
                costumers.business_name.toLowerCase().includes(search.value.toLowerCase()) ||
                costumers.city.toLowerCase().includes(search.value.toLowerCase())
        )
        .sort((a, b) => {
            const lastInspectionDateCompare = (aa: ICostumer, bb: ICostumer) => {
                if (aa.last_inspection_delta && bb.last_inspection_delta) {
                    return bb.last_inspection_delta - aa.last_inspection_delta;
                } else if (aa.last_inspection_delta) {
                    return -1;
                } else if (bb.last_inspection_delta) {
                    return 1;
                }
                return 0;
            };

            if (a.expiration_state === b.expiration_state) {
                return lastInspectionDateCompare(a, b);
            }

            if (a.expiration_state && !b.expiration_state) {
                return -1;
            } else if (!a.expiration_state && b.expiration_state) {
                return 1;
            }

            switch (a.expiration_state) {
                case "expired_situation":
                    return -1;
                case "expiring_situation":
                    switch (b.expiration_state) {
                        case "recent_inspection":
                        case "regular_situation":
                            return -1;
                        case "expiring_situation":
                            return lastInspectionDateCompare(a, b);
                        case "expired_situation":
                            return 1;
                    }
                    break;
                case "regular_situation":
                    switch (b.expiration_state) {
                        case "recent_inspection":
                            return -1;
                        case "regular_situation":
                            return lastInspectionDateCompare(a, b);
                        case "expiring_situation":
                        case "expired_situation":
                            return 1;
                    }
                    break;
                case "recent_inspection":
                    return b.expiration_state === "recent_inspection" ? lastInspectionDateCompare(a, b) : 1;
            }

            return lastInspectionDateCompare(a, b);
        })
);
// .sort((a: ICostumer, b: ICostumer) => {}))
const costumersMarkers = computed(
    () =>
        costumers.value
            .filter((item) => item.latitude && item.longitude && item.expiration_state)
            .map(
                (item) =>
                    ({
                        position: {
                            lat: Number(item.latitude!),
                            lng: Number(item.longitude!),
                        },
                        icon: getMarkerIconUrl(item.expiration_state!),
                        title: item.business_name,
                        clickable: true,
                        infoWindowHTML: `<p>Ultima ispezione: ${item.last_inspection_delta} giorni fa</p>`,
                    } as IExtendedMarkerOptions)
            ) as IExtendedMarkerOptions[]
);
const isOnline = computed(() => networkStore.isOnline);
const headerButtons = computed(() => {
    return [
        {
            id: 1,
            label: "",
            icon: isOnline.value ? cloudDoneOutline : cloudOfflineOutline,
            disabled: !isOnline.value,
            action: async () => inspectionsStore.syncData(),
        },
    ];
});
const openModal = async () => {
    const modal = await modalController.create({
        component: CostumerModal,
        componentProps: {},
    });
    modal.present();

    const audit = await modal.onWillDismiss();
};

const archiveCostumer = async (costumerId: number) => {
    const alert = await alertController.create({
        header: "Confermi di voler archiviare questo cliente?",
        message: "Potrai sempre recuperarlo in seguito, ma non sarà più visibile nella lista dei clienti.",
        buttons: [
            {
                text: "Annulla",
                role: "cancel",
                cssClass: "archive-text-medium",
            },
            {
                text: "Archivia",
                role: "destructive",
                cssClass: "archive-text-danger",
                handler: async () => {
                    try {
                        await costumersStore.archiveCostumer(costumerId);
                        toastController
                            .create({
                                message: "Cliente archiviato con successo",
                                duration: 1500,
                                position: "top",
                                color: "success",
                            })
                            .then((toast) => toast.present());
                        router.push({ name: "costumers" });
                    } catch (error) {
                        console.error("Errore durante l'archiviazione del cliente:", error);
                        const toast = await toastController.create({
                            message: "Errore durante l'archiviazione del cliente",
                            duration: 2000,
                            position: "bottom",
                            color: "danger",
                        });

                        await toast.present();
                        Sentry.captureException(error);
                    }
                },
            },
        ],
    });

    await alert.present();
};

const goToArchivedCostumers = () => {
    costumersStore.loadArchivedCostumers();
    router.push({ name: "archivedCostumers" });
};

const handleRefresh = async (event: CustomEvent) => {
    try {
        await costumersStore.loadCostumers();
        event.detail.complete();
    } catch (error) {
        console.error("Errore durante l'aggiornamento della lista clienti:", error);
        const toast = await toastController.create({
            message: "Errore durante l'aggiornamento della lista clienti",
            duration: 2000,
            position: "bottom",
            color: "danger",
        });
        await toast.present();
        Sentry.captureException(error);
    }
};
</script>

<style lang="scss">
.costumer-list-item {
    position: relative;
    --padding-start: 0;
    --padding-end: 0;
    --inner-padding-start: 16px;
    --inner-padding-end: 16px;
}

.expiration-indicator {
    display: inline-block;
    padding: 3px;
    font-weight: bold;
    border-radius: 4px;

    &.recent_inspection {
        background-color: lightskyblue;
    }

    &.regular_situation {
        background-color: green;
    }

    &.expiring_situation {
        background-color: yellow;
        color: var(--ion-background-color);
    }

    &.expired_situation {
        background-color: red;
        color: var(--ion-background-color);
    }
}

.costumer-list-item-label h2 {
    font-size: 1.3rem;
    white-space: normal;
}
</style>
